<script>
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { searchProductosBySku, searchProductosByDescrip } from '../../lib/erpProductApi.js';

  const dispatch = createEventDispatcher();

  export let mode = 'sku';
  export let value = '';
  export let label = '';
  export let placeholder = '';
  export let disabled = false;
  export let inputId = '';

  let suggestions = [];
  let isOpen = false;
  let isLoading = false;
  let searchError = '';
  let activeIndex = -1;
  let debounceTimer;

  $: searchFn = mode === 'sku' ? searchProductosBySku : searchProductosByDescrip;

  onDestroy(() => {
    clearTimeout(debounceTimer);
  });

  function scheduleSearch(term) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runSearch(term), 280);
  }

  async function runSearch(term) {
    const trimmed = term?.trim();
    if (!trimmed || trimmed.length < 2) {
      suggestions = [];
      isOpen = false;
      searchError = '';
      return;
    }

    isLoading = true;
    searchError = '';

    try {
      suggestions = await searchFn(trimmed, { partial: true });
      isOpen = suggestions.length > 0;
      activeIndex = suggestions.length ? 0 : -1;
    } catch (error) {
      suggestions = [];
      isOpen = false;
      searchError = error.message || 'Error al buscar productos';
    } finally {
      isLoading = false;
    }
  }

  function handleInput() {
    dispatch('input', value);
    scheduleSearch(value);
  }

  function selectProduct(product) {
    value = mode === 'sku' ? product.sku : product.descrip;
    isOpen = false;
    suggestions = [];
    dispatch('select', product);
  }

  function handleKeydown(event) {
    if (!isOpen || !suggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      activeIndex = (activeIndex + 1) % suggestions.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      activeIndex = (activeIndex - 1 + suggestions.length) % suggestions.length;
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      selectProduct(suggestions[activeIndex]);
    } else if (event.key === 'Escape') {
      isOpen = false;
    }
  }

  function handleBlur() {
    setTimeout(() => {
      isOpen = false;
    }, 150);
  }
</script>

<div class="product-ac relative">
  {#if label}
    <label for={inputId} class="block text-xs font-semibold text-blueGray-700 mb-1">{label}</label>
  {/if}
  <input
    id={inputId}
    type="text"
    class="w-full px-3 py-2 border border-blueGray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-blueGray-100"
    {placeholder}
    {disabled}
    autocomplete="off"
    bind:value
    on:input={handleInput}
    on:keydown={handleKeydown}
    on:focus={() => { if (suggestions.length) isOpen = true; }}
    on:blur={handleBlur}
  />
  {#if isLoading}
    <span class="product-ac__hint">Buscando…</span>
  {/if}
  {#if searchError}
    <p class="text-xs text-red-600 mt-1">{searchError}</p>
  {/if}
  {#if isOpen}
    <ul class="product-ac__list" role="listbox">
      {#each suggestions as product, index (product.productoId + product.sku)}
        <li>
          <button
            type="button"
            class="product-ac__option"
            class:product-ac__option--active={index === activeIndex}
            on:mousedown|preventDefault={() => selectProduct(product)}
          >
            <span class="font-mono text-xs font-semibold">{product.sku}</span>
            <span class="text-xs opacity-80 truncate">{product.descrip}</span>
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .product-ac__hint {
    position: absolute;
    right: 0.5rem;
    top: 2rem;
    font-size: 0.65rem;
    color: #64748b;
  }

  .product-ac__list {
    position: absolute;
    z-index: 20;
    left: 0;
    right: 0;
    margin-top: 0.25rem;
    max-height: 11rem;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 0.375rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  .product-ac__option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
    width: 100%;
    text-align: left;
    padding: 0.5rem 0.625rem;
    border: none;
    background: transparent;
    cursor: pointer;
  }

  .product-ac__option:hover,
  .product-ac__option--active {
    background: #fff7ed;
  }
</style>
